// MACHINERY V2 (Stroje) — status STYLE BUILDERS (split out of cardStates.ts
// at M2.2).
//
// WHY THIS FILE EXISTS: cardStates.ts had reached 299 lines, one line under
// the project's 300-line cap, and M2.2 must ADD to its derivation (the new
// `dotConn` field — see deriveCardPresentation). The ticket's hard planning
// constraint requires splitting a near-cap file FIRST, as a discrete step,
// before any new lines land. The style builders are the natural seam: they are
// pure presentation (colour maths + inline style objects), they decide NOTHING
// about state, and they had exactly one consumer (StatusBadge.tsx).
//
// This is a MOVE, not a rewrite — the four exports below are byte-identical to
// the block that sat at cardStates.ts:245-299. They are deliberately NOT
// re-exported from cardStates.ts: cardStateStyles imports types FROM
// cardStates, so a re-export would make the pair circular. The single importer
// was updated instead.
//
// Variants are plain lookup maps to inline style objects (§3: NOT cva).
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import type { CSSProperties } from "react";

import { MachineryV2Status, MachineryV2StatusToken, STATUS_SPECS } from "./cardStates";

const mix = (token: MachineryV2StatusToken, pct: number, base: string): string =>
  "color-mix(in oklab, var(" + token + ") " + pct + "%, var(" + base + "))";

export interface MachineryV2StatusColors {
  fg: string;
  bg: string;
  bd: string;
}

/** Stale drops the fill to 6%, the border to 22% and mixes the ink toward
 *  --muted at 40% — the "last-known, do not trust me" treatment. */
export const statusColors = (
  status: MachineryV2Status,
  stale: boolean
): MachineryV2StatusColors => {
  const { token } = STATUS_SPECS[status];
  return {
    fg: stale ? mix(token, 40, "--muted") : mix(token, 82, "--ink"),
    bg: stale ? mix(token, 6, "--card") : mix(token, 14, "--card"),
    bd: stale ? mix(token, 22, "--border") : mix(token, 38, "--border"),
  };
};

/** Small inline pill — cards, rows, map tiles. */
export const statusChipStyle = (
  status: MachineryV2Status,
  stale: boolean
): CSSProperties => {
  const c = statusColors(status, stale);
  return {
    display: "inline-flex", alignItems: "center", gap: 5, height: 22,
    padding: "0 8px", background: c.bg, border: "1px solid " + c.bd,
    borderRadius: 999, color: c.fg, fontFamily: "var(--font-mono)",
    fontSize: 10.5, letterSpacing: "0.08em", textTransform: "uppercase",
    fontWeight: 700, whiteSpace: "nowrap",
  };
};

/** Full-width band — big-card header and the M2.4 profile hero ribbon. */
export const statusStripStyle = (
  status: MachineryV2Status,
  stale: boolean
): CSSProperties => {
  const c = statusColors(status, stale);
  return {
    height: 32, padding: "0 14px", display: "flex", alignItems: "center",
    gap: 10, background: c.bg, borderBottom: "1px solid " + c.bd, color: c.fg,
    fontFamily: "var(--font-mono)", fontSize: 11.5, letterSpacing: "0.08em",
    textTransform: "uppercase", fontWeight: 700,
  };
};
